import { useContext, useEffect, useReducer, useState } from 'react';

import { UserDetailContext, UserDetailContextProps } from '../../../context/userDetail/userDetailContext';
import { GRID_ACTIONS, gridReducer, IGridReducerState } from '../../../reducer/gridReducer/gridReducer';
import SmartMeterConsentPage from './SmartMeterConsentPage';
import { getSelectedCustomerId } from '../../../helpers/customer';
import { getSmartMeterConsentGridData, giveConsent, refuseConsent } from '../../../common/api/consentApi';
import { useAuthState } from '../../../providers/authProvider';
import { getHildebrandTermsAndConditions } from '../../../common/api/hildebrandApi';
import { toast } from 'react-toastify';
import { getInitialType } from '../../../utils/getInitialTypes';
import { formatDate } from '../../utils/date/date';
import { SearchDetailContext, SearchDetailContextProps } from '../../../context/searchDetail/searchDetailContext';
import { RoutingToTypes } from '../../search/SearchActions/SearchActions';
import { clearHealthCheckCache } from '../../../helpers/axiosCacheHelper';

const SmartMeterConsent = () => {
  const { userDetail } = useContext<UserDetailContextProps>(UserDetailContext);
  const searchContext = useContext<SearchDetailContextProps>(SearchDetailContext);
  const authContext = useAuthState();

  const initialState: IGridReducerState = {
    page: 1,
    currentType: '',
    data: {},
    initialSortField: 'consent',
    initialSortOrder: 'DESC',
    currentSortField: 'consent',
    currentSortOrder: 'ASC',
    isLoading: false,
    hasMoreData: true,
    selectedCustomerId: getSelectedCustomerId(userDetail, true),
  };

  const [termsAndConditions, setTermsAndConditions] = useState<HildebrandApi.RootObject>();

  const [state, dispatch] = useReducer(gridReducer, initialState);

  const formatSmartMeterConsentData = (data: Array<Api.ISmartMeterConsent>) => {
    data.map(item => {
      if (!item.consentExpiry) {
        item.consentExpiry = 'N/A';
      } else {
        item.consentExpiry =
          item.consentExpiry != null && item.consentExpiry.length > 0 ? formatDate(item.consentExpiry) : '';
      }

      item.consentUpdated =
        item.consentUpdated != null && item.consentUpdated.length > 0 ? formatDate(item.consentUpdated) : '';

      if (item.consent === 'Not set') {
        item.consentExpiry = '';
        item.consentUpdated = '';
      }
      return item;
    });
    return data;
  };

  const loadData = async () => {
    dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: true });

    const data = getSmartMeterConsentGridData(process.env.REACT_APP_API_URL, authContext, state.selectedCustomerId);

    data.then((result: Common.IResult<Array<Api.ISmartMeterConsent>>) => {
      if (result && result.success && result.data.length > 0) {
        const data = formatSmartMeterConsentData(result.data);

        data.map((item: Api.ISmartMeterConsent) => {
          if (item.consent === 'Not set' || item.consent === 'Renew') {
            item.warningIcon = true;
          }
        });

        dispatch({ type: GRID_ACTIONS.ADD_DATA, payload: { data: data, page: state.page } });
      } else {
        dispatch({ type: GRID_ACTIONS.CLEAR_DATA });
      }
      dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });
    });
  };

  useEffect(() => {
    if (searchContext.searchDetail != null && searchContext.searchDetail.searchText.length > 0) {
      // filter by search result

      if (
        searchContext.searchDetail.type &&
        searchContext.searchDetail.routingTo === RoutingToTypes.smartMeterConsent
      ) {
        const type = searchContext.searchDetail.type.toLowerCase();

        if (type === 'account') {
          dispatch({
            type: GRID_ACTIONS.FILTER_BY_CUSTOMER,
            payload: { essCustomerId: searchContext.searchDetail.accountId },
          });
        }
        // Clear Search Detail
        searchContext.setSearchDetail({ searchText: '' });
      }
    }
    // TODO:  Might want to remove this and revisit this problem later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchContext.searchDetail.type]);

  // When navigating directly to url
  useEffect(() => {
    if (userDetail.essCustomerId) {
      initialState.currentType = getInitialType(userDetail.hasElectricity, userDetail.hasGas, userDetail.hasRenewable);

      const selectedCustomerAccount = userDetail?.selectedCustomerAccount;
      if (selectedCustomerAccount.length > 0) initialState.selectedCustomerId = selectedCustomerAccount;
      else initialState.selectedCustomerId = getSelectedCustomerId(userDetail, true);

      dispatch({ type: GRID_ACTIONS.REINIT, payload: initialState });
    }
    // TODO:  Might want to remove this and revisit this problem later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetail.hasElectricity, userDetail.hasGas, userDetail.hasRenewable]);

  useEffect(() => {
    dispatch({ type: GRID_ACTIONS.CLEAR_DATA });

    // Don't load if no customer selected
    if (!state.selectedCustomerId) return;

    loadData();
    // TODO:  Might want to remove this and revisit this problem later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedCustomerId]);

  const handleCustomerFilterChange = (option: Common.IOption) => {
    dispatch({ type: GRID_ACTIONS.FILTER_BY_CUSTOMER, payload: { essCustomerId: option.value } });
  };

  const loadTermsAndConditions = async () => {
    const data: HildebrandApi.RootObject = await getHildebrandTermsAndConditions(
      process.env.REACT_APP_API_URL,
      authContext
    );

    if (data != null) {
      setTermsAndConditions(data);
    }
  };

  const handleConsentGiveClick = () => {
    loadTermsAndConditions();
  };

  const handleConsentConfirm = (tcId: string, reference: string, items: Array<Api.ISmartMeterConsent>) => {
    dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: true });

    const essCustomerId = state.selectedCustomerId;
    const mpans = items.map(item => Number(item.mpan));

    const result = giveConsent(process.env.REACT_APP_API_URL, authContext, essCustomerId, tcId, reference, mpans);

    result.then((data: any) => {
      dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });

      if (data.success === false) {
        const message = 'Error calling consent';
        console.error(message);
        toast.error(message, { position: 'bottom-right' });
      } else {
        const url = `${process.env.REACT_APP_API_URL}/consumption/consent-health`;
        clearHealthCheckCache(url, 'GET', 'SmartMeterConsent', userDetail.customerAccounts);
      }

      // Refresh the grid
      loadData();
    });
  };

  const handleConsentRefuse = (items: Array<Api.ISmartMeterConsent>) => {
    dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: true });

    const essCustomerId = state.selectedCustomerId;
    const mpans = items.map(item => Number(item.mpan));

    const result = refuseConsent(process.env.REACT_APP_API_URL, authContext, essCustomerId, mpans);
    result.then((data: any) => {
      dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });

      if (data.success === false) {
        const message = 'Error calling refuse consent';
        console.error(message);
        toast.error(message, { position: 'bottom-right' });
      } else {
        const url = `${process.env.REACT_APP_API_URL}/consumption/consent-health`;
        clearHealthCheckCache(url, 'GET', 'SmartMeterConsent', userDetail.customerAccounts);
      }

      // Refresh the grid
      loadData();
    });
  };

  return (
    <SmartMeterConsentPage
      gridData={state.gridData}
      isLoading={state.isLoading}
      hasMoreData={state.hasMoreData}
      termsAndConditions={termsAndConditions}
      handlePagingChange={() => {}}
      handleCustomerFilterChange={handleCustomerFilterChange}
      handleConsentGiveClick={handleConsentGiveClick}
      handleConsentConfirm={handleConsentConfirm}
      handleConsentRefuse={handleConsentRefuse}
    ></SmartMeterConsentPage>
  );
};

export default SmartMeterConsent;
